import { useEffect, useState } from "react";

interface PublishDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    totalMinutes: number;
    notes: string;
  }) => void | Promise<void>;
  requestId: string | null;
  previewUrl?: string | null;
  elapsedMinutes: number;
  isLoading?: boolean;
}

export function PublishDialog({
  open,
  onClose,
  onSubmit,
  requestId,
  previewUrl,
  elapsedMinutes,
  isLoading,
}: PublishDialogProps) {
  const [totalMinutes, setTotalMinutes] = useState(elapsedMinutes);
  const [notes, setNotes] = useState("");

  // Reset when opened with new elapsed time
  useEffect(() => {
    if (open) {
      setTotalMinutes(elapsedMinutes);
      setNotes("");
    }
  }, [open, elapsedMinutes]);

  if (!open) return null;

  const handleSubmit = () => {
    onSubmit({ totalMinutes, notes });
  };

  return (
    <div className="hops-overlay" onClick={onClose}>
      <div
        className="hops-dialog"
        onClick={(e) => e.stopPropagation()}
        data-testid="panel-publish-dialog"
      >
        <div className="hops-dialog-title">Publish Artwork</div>

        <div
          style={{
            fontSize: "var(--panel-font-size-sm)",
            color: "var(--panel-text-muted)",
            marginBottom: "var(--panel-spacing-md)",
          }}
        >
          Publishing artwork for {requestId}
        </div>

        {/* Preview thumbnail */}
        {previewUrl && (
          <div
            style={{
              marginBottom: "var(--panel-spacing-md)",
              borderRadius: "var(--panel-radius)",
              border: "1px solid var(--panel-border)",
              overflow: "hidden",
            }}
          >
            <img
              src={previewUrl}
              alt="Preview"
              style={{ width: "100%", height: "auto", display: "block" }}
            />
          </div>
        )}

        <div className="hops-field">
          <label className="hops-label">Time Spent (minutes)</label>
          <input
            className="hops-input"
            type="number"
            min={0}
            value={totalMinutes}
            onChange={(e) =>
              setTotalMinutes(Math.max(0, parseInt(e.target.value, 10) || 0))
            }
            data-testid="panel-publish-minutes"
          />
        </div>

        <div className="hops-field">
          <label className="hops-label">Completion Notes (optional)</label>
          <textarea
            className="hops-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes about the completed artwork..."
            rows={3}
            data-testid="panel-publish-notes"
          />
        </div>

        <div className="hops-dialog-actions">
          <button
            className="hops-btn hops-btn-secondary"
            onClick={onClose}
            disabled={isLoading}
            data-testid="panel-publish-cancel"
          >
            Cancel
          </button>
          <button
            className="hops-btn hops-btn-primary"
            onClick={handleSubmit}
            disabled={isLoading}
            data-testid="panel-publish-submit"
          >
            {isLoading ? "Publishing..." : "Publish"}
          </button>
        </div>
      </div>
    </div>
  );
}
